import React, { useState, useEffect } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { useDocument } from '../contexts/DocumentContext';

export const MarkdownEditor: React.FC = () => {
  const { content, setContent, isDirty, lastSaved } = useDocument();
  const [localContent, setLocalContent] = useState(content);

  // Sync with context when content changes externally
  useEffect(() => {
    setLocalContent(content);
  }, [content]);

  // Debounced update to context
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localContent !== content) {
        setContent(localContent);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [localContent, content, setContent]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocalContent(e.target.value);
  };

  const getAutoSaveStatus = () => {
    if (isDirty) {
      return 'Saving...';
    }
    if (lastSaved) {
      const savedDate = new Date(lastSaved);
      const now = new Date();
      const diffSeconds = Math.floor((now.getTime() - savedDate.getTime()) / 1000);

      if (diffSeconds < 60) {
        return 'Auto-saved just now';
      } else if (diffSeconds < 3600) {
        const minutes = Math.floor(diffSeconds / 60);
        return `Auto-saved ${minutes}m ago`;
      } else {
        return `Auto-saved at ${savedDate.toLocaleTimeString()}`;
      }
    }
    return 'Not saved';
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <TextField
        multiline
        fullWidth
        value={localContent}
        onChange={handleChange}
        placeholder="# Write your markdown here...

## Example Heading

- List item 1
- List item 2

**Bold text** and *italic text*

```javascript
// Code block
console.log('Hello World');
```"
        sx={{
          flex: 1,
          '& .MuiInputBase-root': {
            height: '100%',
            alignItems: 'flex-start',
            fontFamily: 'monospace',
            fontSize: '14px',
          },
          '& textarea': {
            height: '100% !important',
            overflow: 'auto !important',
          }
        }}
        InputProps={{
          sx: {
            height: '100%',
            backgroundColor: 'white',
          },
        }}
        inputProps={{
          'aria-label': 'Markdown Editor'
        }}
      />
      <Box
        sx={{
          p: 1,
          borderTop: 1,
          borderColor: 'divider',
          backgroundColor: 'var(--theme-secondary)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="caption" color="var(--theme-on-secondary)">
          {localContent.length} characters
        </Typography>
        <Typography variant="caption" color={isDirty ? 'warning.main' : 'success.main'}>
          {getAutoSaveStatus()}
        </Typography>
      </Box>
    </Box>
  );
};
